// Semi-logical AI controller for bot players. One `BotAI` instance per
// bot. Same pattern as `NpcManager` / `ChestManager` — transport-neutral
// state, no server/client imports.
//
// The AI is a priority tree with short reaction delays and occasional
// suboptimal picks. Not a full behavior tree, not pathfinding — just
// enough to feel alive.

import type { Role } from '../net/protocol';
import { rollThree } from './powerups';
import { VENUES } from './bosses';

export const BOT_TARGET_PER_TEAM = 2;

export type BotSelf = {
  id: string;
  role: Role;
  x: number;
  z: number;
  yaw: number;
  hp: number;
  hpMax: number;
  level: number;
  alive: boolean;
  questMask: number;
};

export type BotOpponent = {
  id: string;
  role: Role;
  x: number;
  z: number;
  hood: boolean;
  alive: boolean;
  hp: number;
};

export type BotNpc = {
  x: number;
  z: number;
  stage: 0 | 1 | 2 | 3;
  shiny: boolean;
};

export type BotBoss = {
  side: Role;
  x: number;
  z: number;
  alive: boolean;
  hp: number;
};

export type BotContext = {
  nowMs: number;
  elapsedMatchSec: number;
  opponents: ReadonlyArray<BotOpponent>;
  allies: ReadonlyArray<BotOpponent>;
  npcs: ReadonlyArray<BotNpc>;
  bosses: ReadonlyArray<BotBoss>;
};

export type BotDecision = {
  dx: number;
  dz: number;
  yaw: number;
  wantHood: boolean;
};

const BOT_TUNING = {
  ENGAGE_SIGHT: 16,
  FARM_SEARCH: 14,
  RING_IDEAL: 3.5,
  // Chad circle is 3u radius; the Virgin ring's dead-zone starts
  // at 4u. Parking inside the dead-zone (<4u) keeps the bot safe from
  // the ring while still inside its own circle-attack range. 0.6 leaves
  // headroom against `atTarget` radius 1.0 — bot lands at 0.5..1.6u
  // from the foe, always inside the dead zone, always in circle range.
  CIRCLE_IDEAL: 0.6,
  HOOD_HP_THRESHOLD: 0.55,
  HOOD_COOLDOWN_MS: 8000,
  FACTION_BASE_VIRGIN:   { x: -85, z: 0 },
  FACTION_BASE_CHAD: { x:  85, z: 0 },
  MAP_BOUNDS: { xMin: -100, xMax: 100, zMin: -50, zMax: 50 },
  TARGET_REFRESH_MS: 1200,
} as const;

// Per-bot personality. Each archetype pulls different levers on the
// same decision tree so a room of 6 bots feels like 6 players with
// different play styles, not 6 copies of one bot. Every personality
// keeps the bot in constant motion — we dropped the dither freeze
// because idle pauses were feeding AFK-farm exploits.
//
// Archetypes:
//  • hunter      — always looking for a fight, rarely retreats
//  • farmer      — camps NPCs, avoids enemies until levelled up
//  • reluctant   — walks venues + NPCs, bails early at low HP
//  • kamikaze    — no retreat, dives inside enemy range every time
//  • pro         — balanced, rarely blunders, engages smartly
//  • opportunist — whatever's closest, flexible priorities
export type BotPersonality =
  | 'hunter'
  | 'farmer'
  | 'reluctant'
  | 'kamikaze'
  | 'pro'
  | 'opportunist';

type TargetKind = 'enemy' | 'boss' | 'npc' | 'venue';

type PersonalityTuning = {
  // HP fraction below which the bot retreats to base. 0 disables
  // retreat entirely (kamikaze fights to the last HP).
  retreatPct: number;
  // Probability the bot picks the runner-up target from its priority
  // list instead of the optimal one. Cheap "mistake" simulation.
  blunderChance: number;
  // Multiplier on the role-specific engage distance. <1 hugs closer,
  // >1 stays further out.
  engageIdealMult: number;
  // Ordered preference for which target kinds to consider. First
  // available option that exists in context gets picked (subject to
  // the blunder roll).
  priority: ReadonlyArray<TargetKind>;
};

const PERSONALITY_TUNING: Record<BotPersonality, PersonalityTuning> = {
  hunter:      { retreatPct: 0.18, blunderChance: 0.10, engageIdealMult: 0.85, priority: ['enemy', 'boss', 'npc', 'venue'] },
  farmer:      { retreatPct: 0.40, blunderChance: 0.20, engageIdealMult: 1.00, priority: ['npc', 'venue', 'enemy', 'boss'] },
  reluctant:   { retreatPct: 0.50, blunderChance: 0.25, engageIdealMult: 1.20, priority: ['venue', 'npc', 'enemy', 'boss'] },
  kamikaze:    { retreatPct: 0.00, blunderChance: 0.12, engageIdealMult: 0.55, priority: ['enemy', 'boss', 'npc', 'venue'] },
  pro:         { retreatPct: 0.25, blunderChance: 0.05, engageIdealMult: 0.90, priority: ['enemy', 'boss', 'npc', 'venue'] },
  opportunist: { retreatPct: 0.32, blunderChance: 0.18, engageIdealMult: 1.00, priority: ['npc', 'enemy', 'venue', 'boss'] },
};

const ALL_PERSONALITIES: ReadonlyArray<BotPersonality> = [
  'hunter', 'farmer', 'reluctant', 'kamikaze', 'pro', 'opportunist',
];

export function randomBotPersonality(): BotPersonality {
  return ALL_PERSONALITIES[Math.floor(Math.random() * ALL_PERSONALITIES.length)];
}

// How often a bot rerolls its archetype, in ms. Clockwork intervals
// feel predictable ("oh the hunter becomes a farmer at exactly 0:45")
// so we sample a fresh random window every time — 20..55 s. The mean
// (~37 s) is long enough for a personality to matter, short enough
// that a single match will usually see each bot shift at least once.
const PERSONALITY_REROLL_MIN_MS = 20_000;
const PERSONALITY_REROLL_MAX_MS = 55_000;

function pickRerollDelayMs(): number {
  return PERSONALITY_REROLL_MIN_MS
    + Math.random() * (PERSONALITY_REROLL_MAX_MS - PERSONALITY_REROLL_MIN_MS);
}

export class BotAI {
  private targetX = 0;
  private targetZ = 0;
  private nextRefreshMs = 0;
  private lastHoodMs = 0;
  // True when the current target was set by an enemy engagement. Kept
  // as a flag so tests + debug tools can introspect it; the dither
  // freeze it used to guard is gone, so every bot now moves on every
  // tick regardless of archetype (constant-motion rule).
  private engagingEnemy = false;
  // Per-bot archetype. Rerolled on a *random* interval (not a fixed
  // clock) so bots don't all flip at the same instant. The initial
  // value is set in the constructor; think() triggers subsequent
  // rerolls when `now >= nextPersonalityRerollMs`.
  personality: BotPersonality;
  private nextPersonalityRerollMs = 0;

  constructor(personality?: BotPersonality) {
    this.personality = personality ?? randomBotPersonality();
    // Schedule the first reroll against wall-clock now; think() will
    // re-sync against its own `ctx.nowMs` on the first call.
    this.nextPersonalityRerollMs = Date.now() + pickRerollDelayMs();
  }

  // Force a new archetype right now + reschedule the next reroll.
  // Exposed so debug tools / tests can exercise the reroll path
  // without waiting the full 20..55 s window.
  rerollPersonality(nowMs = Date.now()): BotPersonality {
    this.personality = randomBotPersonality();
    this.nextPersonalityRerollMs = nowMs + pickRerollDelayMs();
    return this.personality;
  }

  think(_dt: number, self: BotSelf, ctx: BotContext): BotDecision {
    const now = ctx.nowMs;

    if (!self.alive) {
      return { dx: 0, dz: 0, yaw: self.yaw, wantHood: false };
    }

    // Personality drift — random-interval reroll. Happens out-of-band
    // from target refresh so the bot doesn't also re-pick a target on
    // the same tick (feels more natural when behavior shifts between
    // two goal picks, not simultaneously with them).
    if (now >= this.nextPersonalityRerollMs) {
      this.personality = randomBotPersonality();
      this.nextPersonalityRerollMs = now + pickRerollDelayMs();
    }

    if (now >= this.nextRefreshMs || this.atTarget(self)) {
      this.pickTarget(self, ctx);
      this.nextRefreshMs = now + BOT_TUNING.TARGET_REFRESH_MS;
    }

    const dx = this.targetX - self.x;
    const dz = this.targetZ - self.z;
    const dist = Math.hypot(dx, dz);
    const yaw = dist > 0.01 ? Math.atan2(dx, -dz) : self.yaw;

    const hpPct = self.hp / self.hpMax;
    const hoodReady = (now - this.lastHoodMs) > BOT_TUNING.HOOD_COOLDOWN_MS;
    // dt dropped to a fixed tick duration to keep hood-roll fair at
    // 15 Hz regardless of the think-call cadence.
    const HOOD_ROLL_PER_SEC = 0.9;
    const wantHood =
      self.role === 'int' &&
      hoodReady &&
      hpPct < BOT_TUNING.HOOD_HP_THRESHOLD &&
      Math.random() < HOOD_ROLL_PER_SEC / 15;
    if (wantHood) this.lastHoodMs = now;

    if (dist < 0.25) {
      return { dx: 0, dz: 0, yaw, wantHood };
    }
    return { dx: dx / dist, dz: dz / dist, yaw, wantHood };
  }

  // Cheap predicate — true if the next think() call will actually
  // consult ctx.opponents / ctx.allies / ctx.npcs / ctx.bosses.
  // Lets the server skip building those arrays when the bot is just
  // advancing along its existing target vector.
  needsContext(self: BotSelf, nowMs: number): boolean {
    if (!self.alive) return false;
    return nowMs >= this.nextRefreshMs || this.atTarget(self);
  }

  // Call on match reset so the bot forgets its old goal.
  reset(): void {
    this.targetX = 0;
    this.targetZ = 0;
    this.nextRefreshMs = 0;
    this.lastHoodMs = 0;
    this.engagingEnemy = false;
    // Fresh match → fresh archetype + fresh reroll window.
    this.personality = randomBotPersonality();
    this.nextPersonalityRerollMs = Date.now() + pickRerollDelayMs();
  }

  private atTarget(self: BotSelf): boolean {
    const dx = this.targetX - self.x;
    const dz = this.targetZ - self.z;
    return dx * dx + dz * dz < 1.0;
  }

  // Priority tree:
  //  0. HP low → retreat to own base (skipped for kamikaze archetype)
  //  1..N: iterate personality.priority in order, pushing whichever
  //        candidates actually exist this tick.
  //  last: wander — always present so the bot is never idle.
  // `blunderChance` (personality-specific) swaps optimal→runner-up.
  private pickTarget(self: BotSelf, ctx: BotContext): void {
    const tuning = PERSONALITY_TUNING[this.personality];
    const base = self.role === 'int'
      ? BOT_TUNING.FACTION_BASE_VIRGIN
      : BOT_TUNING.FACTION_BASE_CHAD;

    const hpPct = self.hp / self.hpMax;
    if (tuning.retreatPct > 0 && hpPct < tuning.retreatPct) {
      this.engagingEnemy = false;
      this.setTarget(base.x, base.z);
      return;
    }

    const enemy = nearestAlive(ctx.opponents, self.x, self.z, BOT_TUNING.ENGAGE_SIGHT);
    const enemyBoss = ctx.bosses.find((b) => b.alive && b.side !== self.role) ?? null;
    const farmNpc = nearestFarmable(ctx.npcs, self.x, self.z, BOT_TUNING.FARM_SEARCH);
    const venue = pickVenueTarget(self, ctx.elapsedMatchSec);

    // Walk the personality's priority order, collecting an action for
    // the first target-kind that's available in this tick. Unlike the
    // fixed-order table before, this lets the `farmer` prefer NPCs
    // over enemies and the `reluctant` prefer venues over combat.
    const options: Array<() => void> = [];
    for (const kind of tuning.priority) {
      if (kind === 'enemy' && enemy) {
        options.push(() => { this.engagingEnemy = true; this.setEngagePoint(self, enemy, tuning.engageIdealMult); });
      } else if (kind === 'boss' && enemyBoss && hpPct > 0.5) {
        options.push(() => { this.engagingEnemy = true; this.setEngagePoint(self, enemyBoss, tuning.engageIdealMult); });
      } else if (kind === 'npc' && farmNpc) {
        options.push(() => { this.engagingEnemy = true; this.setEngagePoint(self, farmNpc, tuning.engageIdealMult); });
      } else if (kind === 'venue' && venue) {
        options.push(() => { this.engagingEnemy = false; this.setTarget(venue.x, venue.z); });
      }
    }
    // Wander sits at the bottom as a never-null fallback — the bot is
    // never idle, just might not have a good goal.
    options.push(() => {
      this.engagingEnemy = false;
      const nearMid = (Math.random() - 0.5) * 60;
      const driftZ = (Math.random() - 0.5) * 40;
      this.setTarget(nearMid, driftZ);
    });

    const blunder = Math.random() < tuning.blunderChance;
    const idx = (blunder && options.length > 1) ? 1 : 0;
    options[idx]();
  }

  // Park the target at the bot's weapon sweet spot relative to the foe —
  // inside circle radius for Chad, in the ring band for Virgin.
  // `idealMult` comes from the personality tuning so kamikaze archetypes
  // hug closer than reluctant ones.
  private setEngagePoint(self: BotSelf, foe: { x: number; z: number }, idealMult = 1): void {
    const baseIdeal = self.role === 'ext'
      ? BOT_TUNING.CIRCLE_IDEAL
      : BOT_TUNING.RING_IDEAL;
    const ideal = Math.max(0.2, baseIdeal * idealMult);
    const fx = foe.x - self.x;
    const fz = foe.z - self.z;
    const d = Math.hypot(fx, fz);
    if (d < 0.001) {
      this.setTarget(foe.x, foe.z);
      return;
    }
    this.setTarget(foe.x - (fx / d) * ideal, foe.z - (fz / d) * ideal);
  }

  private setTarget(x: number, z: number): void {
    this.targetX = clamp(x, BOT_TUNING.MAP_BOUNDS.xMin, BOT_TUNING.MAP_BOUNDS.xMax);
    this.targetZ = clamp(z, BOT_TUNING.MAP_BOUNDS.zMin, BOT_TUNING.MAP_BOUNDS.zMax);
  }
}

// ---- Helpers ----

function nearestAlive<T extends { x: number; z: number; alive: boolean; hood?: boolean }>(
  list: ReadonlyArray<T>,
  x: number,
  z: number,
  maxDist: number,
): T | null {
  let best: T | null = null;
  let bestDist = maxDist * maxDist;
  for (const e of list) {
    if (!e.alive) continue;
    if (e.hood) continue; // can't see hooded Virgin
    const dx = e.x - x;
    const dz = e.z - z;
    const d = dx * dx + dz * dz;
    if (d < bestDist) { bestDist = d; best = e; }
  }
  return best;
}

function nearestFarmable(
  list: ReadonlyArray<BotNpc>,
  x: number,
  z: number,
  maxDist: number,
): BotNpc | null {
  let best: BotNpc | null = null;
  let bestDist = maxDist * maxDist;
  for (const n of list) {
    // Stage 0-1 NPCs give XP safely; stage 2+ turn hostile.
    if (n.stage > 1) continue;
    const dx = n.x - x;
    const dz = n.z - z;
    const d = dx * dx + dz * dz;
    if (d < bestDist) { bestDist = d; best = n; }
  }
  return best;
}

// Return the next unvisited venue for this team whose time-gate has
// elapsed. Uses `VENUES` + the bot's `questMask` (same bitfield the
// real quest tracker writes to).
function pickVenueTarget(self: BotSelf, elapsedMatchSec: number): { x: number; z: number } | null {
  const team = VENUES.filter((v) => v.role === self.role);
  const visited = popCount(self.questMask);
  const firstGate = 60;
  const secondGate = 180;
  const gate = visited === 0 ? firstGate : secondGate;
  if (elapsedMatchSec < gate) return null;
  for (let i = 0; i < team.length; i++) {
    const bit = 1 << i;
    if ((self.questMask & bit) === 0) {
      return { x: team[i].x, z: team[i].z };
    }
  }
  return null;
}

function popCount(n: number): number {
  let c = 0;
  while (n) { c += n & 1; n >>>= 1; }
  return c;
}

function clamp(v: number, lo: number, hi: number): number {
  return v < lo ? lo : v > hi ? hi : v;
}

// ---- Level-up auto-pick ----

// Pick a random powerup id from the eligible pool. Used by the server
// and SoloStub when a bot levels up — bots don't open the pick modal.
export function botPickPowerup(role: Role, ownedOneTime: ReadonlySet<string>): string | null {
  const rolled = rollThree(role, ownedOneTime);
  if (rolled.length === 0) return null;
  return rolled[Math.floor(Math.random() * rolled.length)].id;
}
